export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  PACKER = 'packer'
}

export interface UserProfile {
  id: string;
  email?: string;
  role: UserRole;
  name: string;
  mobile?: string; // For packers
  pin?: string; // Encrypted or checked via Auth
  organization_id?: string;
  credits?: number;
  
  // --- NEW: Admin Billing Details ---
  admin_organization?: string;
  admin_website?: string;
  admin_gst?: string;
  admin_address?: string;
  admin_city?: string;
  admin_state?: string;
  admin_pincode?: string;
  admin_country?: string;

  company_details?: CompanyDetails; // Keeping for backward compatibility if needed
  integrations?: IntegrationConfig;
  created_at?: string;
}

export interface CompanyDetails {
  name: string;
  gst: string;
  address: string;
}

export interface IntegrationConfig {
  googleDriveConnected: boolean;
  googleSheetConnected: boolean;
  whatsappProvider: 'Interakt' | 'Wati' | 'None';
  ecommercePlatform: 'Shopify' | 'WooCommerce' | 'BigCommerce' | 'None';
}

export interface VideoLog {
  id: string;
  awb: string;
  packer_id: string;
  admin_id: string;
  created_at: string;
  video_url: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  whatsapp_status: 'pending' | 'sent' | 'failed';
  customer_phone?: string;
  order_id?: string;
  // Joins
  packer_name?: string; 
}

export interface CreditRequest {
  id: string;
  admin_id: string;
  admin_name?: string; // Joined
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

// =========================================
// REVIEW SYSTEM TYPES
// ==========================================

export enum ReviewStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export enum ReviewSource {
  DIRECT = 'direct',
  WHATSAPP = 'whatsapp',
  EMAIL = 'email',
  IMPORT = 'import'
}

export interface Review {
  id: string;
  admin_id: string;
  product_id: string;
  order_id: string | null;
  
  // Review content
  rating: number; // 1-5
  content: string;
  reviewer_name: string;
  reviewer_email: string | null;
  reviewer_phone: string | null;
  
  // Media
  media_urls: ReviewMedia[];
  
  // Status & verification
  status: ReviewStatus;
  verified_purchase: boolean;
  source: ReviewSource;
  
  // Engagement
  helpful_count: number;
  
  // Admin response
  response_text: string | null;
  response_at: string | null;
  
  // 🆕 Product metadata (for tag-based grouping)
  product_tags?: string[];      // Shopify product tags
  product_type?: string;         // Shopify product type
  
  // Metadata
  token_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReviewMedia {
  type: 'image' | 'video';
  url: string;
  thumbnail_url?: string;
}

export interface ReviewToken {
  token: string;
  admin_id: string;
  order_id: string;
  product_id: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  expires_at: string;
  used_at: string | null;
  created_at: string;
}

export interface ReviewRequestLog {
  id: string;
  admin_id: string;
  order_id: string;
  channel: 'whatsapp' | 'email' | 'both';
  sent_at: string;
  opened_at: string | null;
  submitted_at: string | null;
  status: 'pending' | 'sent' | 'opened' | 'submitted' | 'failed';
  token_id: string | null;
  created_at: string;
}

export interface ScheduledReview {
  id: string;
  admin_id: string;
  order_id: string;
  product_id: string;
  product_name: string;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  scheduled_for: string;
  status: 'pending' | 'sent' | 'completed' | 'failed';
  sent_at: string | null;
  token_id: string | null;
  created_at: string;
}

export interface WidgetSettings {
  admin_id: string;
  
  // Layout
  layout: 'grid' | 'carousel' | 'list' | 'snippets';  // 🆕 Added 'snippets'
  reviews_per_page: number;
  
  // Styling
  star_color: string;
  card_style: 'minimal' | 'bordered' | 'shadow';
  show_verified_badge: boolean;
  
  // Badge
  badge_enabled: boolean;
  badge_position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  badge_style: 'compact' | 'detailed';
  
  // Filtering
  min_rating_display: number; // Only show reviews >= this rating
  show_photos_only: boolean;
  
  // Carousel settings
  autoplay: boolean;
  autoplay_speed: number; // seconds
  
  // 🆕 Snippets settings
  snippets_photos?: number;                     // Number of photos (8, 10, 12, 15)
  snippets_layout?: '5x2' | '4x2' | '3x3';      // Grid layout
  snippets_speed?: number;                       // Text rotation speed (ms)
  snippets_photo_source?: 'all' | 'verified' | 'top_rated' | 'recent';  // Photo filter
  snippets_show_stars?: boolean;                 // Show stars in text
  snippets_show_names?: boolean;                 // Show customer names
  snippets_container_style?: 'bordered' | 'shadow';  // Container style
  snippets_rounded_corners?: boolean;            // Rounded corners
  
  // 🆕 Product Grouping (Tag-based review sharing)
  group_by_shopify_tags?: boolean;              // Enable tag-based grouping
  grouping_tag_key?: 'first' | 'product_type' | 'category';  // Which tag to use
  max_grouped_reviews?: number;                 // Max reviews to fetch (default: 300)
  
  // Custom CSS
  custom_css: string | null;
  
  created_at: string;
  updated_at: string;
}

export interface ReviewProductTag {
  id: string;
  admin_id: string;
  tag_name: string;
  product_ids: string[]; // Array of product IDs that share reviews
  created_at: string;
}

export interface ReviewImport {
  id: string;
  admin_id: string;
  source: 'judgeme' | 'loox' | 'custom';
  total_imported: number;
  successful: number;
  failed: number;
  import_data: any; // JSON data from CSV
  status: 'processing' | 'completed' | 'failed';
  created_at: string;
}

export interface ReviewStats {
  total: number;
  average: number;
  rating_distribution: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
  pending_count: number;
  verified_count: number;
}

export interface ReviewSubmissionData {
  token?: string; // For magic link submissions
  admin_id?: string; // For direct submissions
  product_id: string;
  order_id?: string;
  rating: number;
  content: string;
  reviewer_name: string;
  reviewer_email?: string;
  reviewer_phone?: string;
  media_files?: File[]; // Will be uploaded separately
}

// --- Inventory Management Types ---

export type SupportedPlatform = 'shopify' | 'woocommerce' | 'bigcommerce';

export interface InventorySettings {
  platform: SupportedPlatform;
  isEnabled: boolean;
  autoDraftProduct: boolean;      // Draft product if all variants are 0
  autoActiveProduct: boolean;     // Active product if any variant > 0
  autoDeleteVariant: boolean;     // Delete variant if stock is 0
  hideVariantsCss: boolean;       // Inject CSS to hide variants
  lowStockThreshold: number;      // e.g., Notify when stock is below 5
  syncFrequency?: 'realtime' | 'hourly'; // Optional future proofing
}

export interface InventoryLog {
  id: string;
  product_name: string;
  action_type: 'DRAFTED' | 'ACTIVATED';
  details: string;
  created_at: string;
}

// ==========================================
// ORDER UPDATE NOTIFICATION TYPES
// ==========================================

export interface OrderNotificationSettings {
  admin_id: string;
  order_placed_prepaid_enabled: boolean;
  order_placed_prepaid_template: string;
  order_placed_cod_enabled: boolean;
  order_placed_cod_template: string;
  order_fulfilled_enabled: boolean;
  order_fulfilled_template: string;
  order_delivered_enabled: boolean;
  order_delivered_template: string;
  created_at: string;
  updated_at: string;
}
